import { cn } from "../shared/ui/index.ts";
import * as React from "react";
import { useRouterState } from "@tanstack/react-router";
import { PRACTITIONER_NAME } from "../core/app-meta.ts";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import { usePractice } from "../core/providers/practice.provider.tsx";
import { patientsService } from "../core/services/patients.service.ts";
import { I } from "../shared/icons/icons.tsx";
import { PatientAvatar } from "../shared/patient/patient-avatar.component.tsx";
import { searchIncludes } from "../shared/text-search.ts";
import {
  MESSAGE_CONVERSATIONS,
  MESSAGE_THREADS,
  type ChatMessage,
} from "./conversations.ts";
import {
  inputPlain,
  inputSearch,
  miniIconBtn,
  pgPadX,
  secondaryBtnLg,
} from "../shared/page-layout.ts";

function ConversationAvatar({
  name,
  color,
  avatar,
  online,
  size = 40,
}: {
  name: string;
  color: string;
  avatar: string;
  online?: boolean;
  size?: number;
}) {
  const { practice } = usePractice();
  const patient = patientsService.findByName(name, practice.uuid);
  if (patient) {
    return <PatientAvatar patient={patient} size={size} online={online} />;
  }
  return (
    <div className="relative">
      <div
        className="flex h-10 w-10 items-center justify-center rounded-full text-[13px] font-semibold text-white"
        style={{ background: color }}
      >
        {avatar}
      </div>
      {online && (
        <div className="absolute bottom-0 right-0 h-[11px] w-[11px] rounded-full border-2 border-white bg-sf-success" />
      )}
    </div>
  );
}

export function MessagesPage() {
  const conversations = MESSAGE_CONVERSATIONS;
  const conversationFromUrl = useRouterState({
    select: (s) => {
      const raw = new URLSearchParams(s.location.searchStr).get("conversation");
      const id = raw ? Number.parseInt(raw, 10) : NaN;
      return Number.isFinite(id) ? id : undefined;
    },
  });
  const [selected, setSelected] = React.useState(1);
  const [mobileShowChat, setMobileShowChat] = React.useState(false);

  React.useEffect(() => {
    if (
      conversationFromUrl != null &&
      conversations.some((c) => c.id === conversationFromUrl)
    ) {
      setSelected(conversationFromUrl);
      setMobileShowChat(true);
    }
  }, [conversationFromUrl, conversations]);
  const [query, setQuery] = React.useState("");
  const conv = conversations.find((c) => c.id === selected);
  const toast = Interactions.useToast();

  const [allMessages, setAllMessages] = React.useState<
    Record<number, ChatMessage[]>
  >(() => ({ ...MESSAGE_THREADS }));
  const msgs = allMessages[selected] || [];
  const [draft, setDraft] = React.useState("");
  const filteredConvs = conversations.filter(
    (c) => searchIncludes(c.name, query) || searchIncludes(c.preview, query),
  );

  const sendMessage = () => {
    if (!draft.trim()) return;
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    setAllMessages((prev) => ({
      ...prev,
      [selected]: [
        ...(prev[selected] || []),
        { from: "practice", text: draft.trim(), time },
      ],
    }));
    setDraft("");
    toast.push({
      type: "success",
      title: "Message envoyé",
      message: `À ${conv?.name}`,
    });
  };

  const selectConversation = (id: number) => {
    setSelected(id);
    setMobileShowChat(true);
  };

  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,320px)_1fr]">
      <aside
        className={cn(
          "flex min-h-0 flex-col border-r border-sf-border bg-white",
          mobileShowChat ? "hidden lg:flex" : "flex",
        )}
      >
        <div className={cn("border-b border-sf-border py-3", pgPadX)}>
          <div className={inputSearch}>
            <I.Search size={14} className="text-sf-text-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher une conversation…"
              className={cn(inputPlain, "text-[13px]")}
            />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {filteredConvs.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => selectConversation(c.id)}
              className={cn(
                "grid w-full cursor-pointer grid-cols-[40px_1fr_auto] items-center gap-2.5 border-none px-3.5 py-2.5 text-left font-[inherit] transition-colors",
                selected === c.id
                  ? "border-l-[3px] border-l-sf-primary bg-sf-primary-50"
                  : "border-l-[3px] border-l-transparent bg-transparent hover:bg-gray-50",
              )}
            >
              <ConversationAvatar
                name={c.name}
                color={c.color}
                avatar={c.avatar}
                online={c.online}
              />
              <div className="min-w-0">
                <div
                  className={cn(
                    "text-[13px] text-sf-text",
                    c.unread > 0 ? "font-bold" : "font-semibold",
                  )}
                >
                  {c.name}
                </div>
                <div
                  className={cn(
                    "truncate text-xs",
                    c.unread > 0
                      ? "font-medium text-sf-text"
                      : "text-sf-text-muted",
                  )}
                >
                  {c.preview}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="text-[11px] text-sf-text-muted">{c.time}</div>
                {c.unread > 0 && (
                  <div className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-sf-primary px-1.5 text-[10.5px] font-bold text-white">
                    {c.unread}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </aside>

      <main
        className={cn(
          "flex min-h-0 flex-col bg-sf-surface-alt",
          !mobileShowChat && "hidden lg:flex",
        )}
      >
        {conv && (
          <>
            <div
              className={cn(
                "flex items-center justify-between border-b border-sf-border bg-white py-3",
                pgPadX,
              )}
            >
              <div className="flex min-w-0 items-center gap-3">
                <button
                  type="button"
                  onClick={() => setMobileShowChat(false)}
                  className={cn(miniIconBtn(), "lg:hidden")}
                  aria-label="Retour aux conversations"
                >
                  <I.ChevronLeft size={16} />
                </button>
                <ConversationAvatar
                  name={conv.name}
                  color={conv.color}
                  avatar={conv.avatar}
                  online={conv.online}
                />
                <div>
                  <div className="text-sm font-semibold">{conv.name}</div>
                  <div
                    className={cn(
                      "text-[11.5px]",
                      conv.online ? "text-sf-success" : "text-sf-text-muted",
                    )}
                  >
                    {conv.online ? "● En ligne" : "Hors ligne"}
                  </div>
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  className={miniIconBtn()}
                  title="Téléphone"
                >
                  <I.Phone size={14} />
                </button>
                <button type="button" className={miniIconBtn()} title="Dossier">
                  <I.ClipboardList size={14} />
                </button>
                <button type="button" className={miniIconBtn()} title="Plus">
                  <I.Dots size={14} />
                </button>
              </div>
            </div>

            <div
              className={cn(
                "flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto py-4",
                pgPadX,
              )}
            >
              {msgs.map((m, i) => {
                const isPractice = m.from === "practice";
                const senderLabel = isPractice ? PRACTITIONER_NAME : conv.name;
                const showSender = i === 0 || msgs[i - 1]?.from !== m.from;

                return (
                  <div
                    key={i}
                    className={cn(
                      "flex max-w-[min(85%,20rem)] flex-col gap-1 sm:max-w-[70%]",
                      isPractice
                        ? "items-end self-end"
                        : "items-start self-start",
                    )}
                  >
                    {showSender && (
                      <span
                        className={cn(
                          "px-1 text-[11px] font-semibold leading-none",
                          isPractice
                            ? "text-sf-primary"
                            : "text-sf-text-secondary",
                        )}
                      >
                        {senderLabel}
                      </span>
                    )}
                    <div
                      className={cn(
                        "px-3 py-2.5 text-[13.5px] leading-snug shadow-[var(--sf-shadow-sm)]",
                        isPractice
                          ? "rounded-xl rounded-br-[4px] bg-sf-primary text-white"
                          : "rounded-xl rounded-bl-[4px] border border-sf-border bg-white text-sf-text",
                      )}
                    >
                      {m.text}
                    </div>
                    <div
                      className={cn(
                        "px-1 text-[10.5px] text-sf-text-muted",
                        isPractice && "text-right",
                      )}
                    >
                      {m.time}
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              className={cn("border-t border-sf-border bg-white py-3", pgPadX)}
            >
              <div className="flex items-end gap-2 rounded-lg border border-sf-border bg-sf-surface-alt p-2 focus-within:border-sf-primary focus-within:shadow-[0_0_0_3px_color-mix(in_srgb,var(--sf-primary)_12%,transparent)]">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="Écrire un message… (Entrée pour envoyer)"
                  rows={1}
                  className="max-h-[120px] min-h-6 flex-1 resize-none border-none bg-transparent font-[inherit] text-[13.5px] text-sf-text outline-none"
                />
                <button
                  type="button"
                  onClick={sendMessage}
                  disabled={!draft.trim()}
                  className={cn(
                    secondaryBtnLg(),
                    !draft.trim() && "cursor-not-allowed opacity-50",
                  )}
                >
                  Envoyer
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
